import { Autocomplete, TextField } from '@mui/material';

import { Checkbox } from '@design-system/components/checkbox/checkbox';

import type { BundleOption } from '../../model/AutomationBundles.model';
import type { AutomationDetailsViewModel } from '../../view-model/AutomationDetails.viewModel';
import { useOtpPitchTargetConfigViewModel } from '../../view-model/OtpPitchTargetConfig.viewModel';
import * as styles from '../Automations.view.css';

type OtpPitchTargetConfigViewProps = {
  creatorId: number;
  details: AutomationDetailsViewModel;
};

/** Not in the Figma: the backend needs a pitch target once a creator sells
 *  more than one subscription, so the form exists only for that case. */
export const OtpPitchTargetConfigView = ({
  creatorId,
  details,
}: OtpPitchTargetConfigViewProps) => {
  const vm = useOtpPitchTargetConfigViewModel(creatorId, details);
  const optionList: BundleOption[] =
    vm.options.type === 'fetched' ? vm.options.value : [];
  const selected: BundleOption | null = vm.selectedBundleId
    ? (optionList.find((option) => option.id === vm.selectedBundleId) ?? {
        id: vm.selectedBundleId,
        title: `Subscription #${vm.selectedBundleId}`,
      })
    : null;
  const loadError = vm.options.type === 'fetch-error' ? vm.options.value : null;

  return (
    <div className={styles.configForm} data-testid="otp-config">
      {vm.showPicker ? (
        <div className={styles.configCard}>
          <div className={styles.configCardHead}>
            <span className={styles.configCardTitle}>
              Which subscription should we pitch?
            </span>
            <span className={styles.requiredTag}>Required</span>
          </div>
          <span className={styles.configCardHint}>
            You sell more than one subscription, so tell us which one the emails
            and pushes should point at.
          </span>
          <Autocomplete<BundleOption, false, false, false>
            options={optionList}
            value={selected}
            loading={vm.options.type === 'fetching'}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )}
            onChange={(_event, option) => vm.selectBundle(option?.id ?? null)}
            noOptionsText={
              loadError
                ? "We couldn't load your subscriptions."
                : 'No published subscriptions'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Choose a published subscription"
                inputProps={{
                  ...params.inputProps,
                  'aria-label': 'Which subscription should we pitch?',
                }}
                error={Boolean(vm.validation)}
                helperText={vm.validation}
              />
            )}
          />
          {loadError ? (
            <button
              type="button"
              className={styles.viewInsights}
              onClick={() => void loadError.retry()}
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : vm.singleBundleNote ? (
        <div className={styles.confirmation}>{vm.singleBundleNote}</div>
      ) : null}

      <div className={styles.configCard}>
        <div className={styles.configCardHead}>
          <span className={styles.configCardTitle}>Push notifications</span>
          <span className={styles.optionalTag}>Optional</span>
        </div>
        <span className={styles.configCardHint}>
          Two pushes back up the emails. They only reach members who allowed
          notifications.
        </span>
        <Checkbox
          label="Include the 2 push notifications"
          checked={vm.includePushes}
          onChange={vm.setIncludePushes}
          testId="otp-include-pushes"
        />
      </div>
    </div>
  );
};
